import { ColumnDef, StringOrTemplateHeader } from '@tanstack/react-table';
import { useTableStoreContext } from '@/components/table/contexts/TableContext.tsx';
import { QueryKey, useQuery } from '@tanstack/react-query';
import {
  FieldDefinitionDto,
  TableDefinitionDto,
} from '@/components/table/types/TableDefinitionDto.ts';
import { useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { fieldDisplayNames } from '@/components/table/_fieldDefinitions/fieldDisplayNames.ts';
import 'dayjs/locale/pl';
import { fieldsConfig } from '@/components/table/_fieldDefinitions/fieldsConfig.ts';
import PrimitiveCheckbox from '@/components/form/_primitives/PrimitiveCheckbox.tsx';
import RowContextMenu, {
  RowContextMenuItemParams,
} from '@/components/table/components/RowContextMenu.tsx';
import { RowWithId } from '@/components/table/types/RowWithId.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { DropdownFieldDefinitionDto } from '@/components/table/_fieldDefinitions/dropdown/DropdownFieldDefinitionDto.ts';
import {
  dropdownFieldRender,
  dropdownFieldRenderOptions,
} from '@/components/table/_fieldDefinitions/dropdown/dropdownFieldRender.tsx';
import { EnumFieldDefinitionDto } from '@/components/table/_fieldDefinitions/enum/EnumFieldDefinitionDto.ts';
import { NestedObjectListFieldDefinitionDto } from '@/components/table/_fieldDefinitions/nestedObjectList/NestedObjectListFieldDefinitionDto.ts';
import { IceCream } from 'lucide-react';
import {
  isVisibleColumn,
  OPTIONS_COLUMN_ID,
  SELECT_COLUMN_ID,
} from '@/components/table/preferences/columnPreferences.ts';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';
import { getCountryName } from '@/utils/countryUtils.ts';
import '@/components/table/types/columnMeta.ts';
import {
  isPrimaryField,
  resolveFieldImportance,
} from '@/components/table/preferences/fieldImportance.ts';
import { EmptyValue } from '@/components/ui/EmptyValue.tsx';
import { tourNameLinkFieldRender } from '@/components/table/_fieldDefinitions/tourName/tourNameLinkFieldRender.tsx';
import { getTransactionMoneyRenders } from '@/components/table/_fieldDefinitions/moneyValue/transactionMoneyFieldRenders.tsx';

const useColumns = <T extends RowWithId>({
  queryKey,
  fetchTableDef,
  allowBulkActions,
  rowContextMenuItems,
  tagTargetType,
}: {
  queryKey: QueryKey;
  fetchTableDef: () => Promise<TableDefinitionDto>;
  allowBulkActions: boolean;
  rowContextMenuItems?: RowContextMenuItemParams<T>[];
  tagTargetType?: TaggableType;
}) => {
  const setTableDef = useTableStoreContext((state) => state.setTableDefinition);

  const { data, isLoading, error } = useQuery({
    queryKey: [...queryKey, 'table-definition'],
    queryFn: fetchTableDef,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Sync field definitions to context
  useEffect(() => {
    if (data) {
      setTableDef(data);
    }
  }, [data, setTableDef]);

  // Handle error
  useEffect(() => {
    if (error) {
      toast.error('Nie można załadowac tabeli', {
        description: (error as Error).message,
      });
    }
  }, [error]);

  // Derive columns from data
  const columns = useMemo(() => {
    if (!data) return undefined;

    const columnDefs = buildColumns<T>(data.fields, queryKey, tagTargetType);

    if (allowBulkActions) {
      const bulkSelectColumn: ColumnDef<T> = {
        id: SELECT_COLUMN_ID,
        enableHiding: false,
        enableResizing: false,
        header: ({ table }) => (
          <div className="flex items-center justify-center">
            <PrimitiveCheckbox
              checked={table.getIsAllPageRowsSelected()}
              indeterminate={table.getIsSomePageRowsSelected()}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
              wrapperClassName="mt-0"
              className="border-neutral-200"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-center overflow-visible">
            <PrimitiveCheckbox
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
              wrapperClassName="mt-0"
              className="border-neutral-200"
            />
          </div>
        ),
        size: 36,
        minSize: 36,
        maxSize: 36,
      };
      columnDefs.unshift(bulkSelectColumn);
    }

    if (rowContextMenuItems !== undefined) {
      const optionsColumn: ColumnDef<T> = {
        id: OPTIONS_COLUMN_ID,
        header: '',
        enableHiding: false,
        enableResizing: false,
        minSize: 28,
        maxSize: 28,
        size: 28,
        cell: (info) => {
          return (
            <RowContextMenu
              rowData={info.row.original}
              rowContextMenuItems={rowContextMenuItems}
            />
          );
        },
      };
      columnDefs.push(optionsColumn);
    }

    return columnDefs;
  }, [data, allowBulkActions, rowContextMenuItems, queryKey, tagTargetType]);

  return {
    columns,
    isLoading,
  };
};

function buildColumns<T>(
  fieldDefinitions: FieldDefinitionDto[],
  queryKey: QueryKey,
  tagTargetType?: TaggableType,
): ColumnDef<T>[] {
  const visibleFields = fieldDefinitions.filter(isVisibleColumn);
  const importance = resolveFieldImportance(fieldDefinitions);

  return visibleFields.map((field) => {
    let fieldId = field.fieldId;
    let accessorKey = fieldId;
    const filedConfig = fieldsConfig[field.type];
    if (filedConfig?.fieldCellRender === undefined) {
      console.error(`No cell renderer defined for field type ${field.type}`);
    }
    let cellRenderer = filedConfig?.fieldCellRender;
    if (field.customField) {
      const customFieldId = field.fieldId.split('.')[1];
      fieldId = `customValue.${customFieldId}`;
      accessorKey = `customValues.${customFieldId}`;
      if (field.type === FieldType.DROPDOWN) {
        const dropdownField = field as DropdownFieldDefinitionDto;
        cellRenderer = (info) =>
          fieldsConfig[field.type].fieldCellRender(info, {
            options: dropdownField.options || [],
          });
      }
      if (field.type === FieldType.ENUM) {
        const enumField = field as EnumFieldDefinitionDto;
        cellRenderer = (info) =>
          fieldsConfig[field.type].fieldCellRender(info, {
            enumSimpleName: enumField.enumName,
          });
      }
    }
    if (field.fieldId.includes('optionValue')) {
      const optionFieldId = field.fieldId.split('.')[1];
      fieldId = `optionValue.${optionFieldId}`;
      accessorKey = `selectedOptions.${optionFieldId}`;
      const dropdownField = field as DropdownFieldDefinitionDto;
      cellRenderer = (info) =>
        dropdownFieldRenderOptions(info, {
          options: dropdownField.options || [],
        });
    }
    if (!field.customField && field.type === FieldType.ENUM) {
      const enumField = field as EnumFieldDefinitionDto;
      cellRenderer = (info) =>
        fieldsConfig[field.type].fieldCellRender(info, {
          enumSimpleName: enumField.enumName,
        });
    }
    if (field.type === FieldType.TAGS) {
      cellRenderer = (info) =>
        fieldsConfig[field.type].fieldCellRender(info, {
          targetType: tagTargetType,
          queryKey,
        });
    }
    if (field.type === FieldType.NESTED_OBJECT_LIST) {
      const nestedListField = field as NestedObjectListFieldDefinitionDto;
      cellRenderer = (info) =>
        fieldsConfig[field.type].fieldCellRender(info, {
          itemType: nestedListField.itemType,
        });
    }
    // Nazwa wyjazdu jest kolumną tekstową (po płaskim polu da się sortować),
    // ale gdy wiersz niesie `tour`, ma prowadzić do wyjazdu.
    if (field.fieldId === 'tourName' && field.type === FieldType.TEXT) {
      cellRenderer = tourNameLinkFieldRender;
    }
    const transactionMoneyRenders = getTransactionMoneyRenders(field);
    if (transactionMoneyRenders) {
      cellRenderer = transactionMoneyRenders.fieldCellRender;
    }
    if (field.fieldId === 'destinationCountry') {
      // backend sends the ISO 3166-1 alpha-2 code; the display list is frontend-owned
      cellRenderer = (info) => {
        const code = info.getValue() as string | undefined;
        return code ? getCountryName(code) : <EmptyValue />;
      };
    }
    return {
      id: fieldId,
      accessorKey: accessorKey,
      header: getHeaderDisplayName(field),
      cell: cellRenderer,
      size: field.colSize,
      minSize: 80,
      enableResizing: true,
      enableHiding:
        (field.hideable ?? true) && !isPrimaryField(field, importance),
      meta: {
        align: filedConfig?.align,
        importance: importance.get(field.fieldId),
      },
    };
  });
}

export function getHeaderDisplayName(fieldDef: FieldDefinitionDto): string {
  if (
    (fieldDef.customField || fieldDef.fieldId.includes('optionValue')) &&
    fieldDef.fieldName
  ) {
    return fieldDef.fieldName;
  }
  return fieldDisplayNames[fieldDef.fieldId] ?? fieldDef.fieldId;
}

export default useColumns;
